import { useEffect } from "react";
import { ApiError } from "@ferrivia/api-contract";
import { useApi, useSession } from "@ferrivia/auth-web";

export interface UseEnsurePatientRelationshipOptions {
  /** Reported so the shell-level offline banner has something besides
      `navigator.onLine` to go on — see packages/ui/src/useOnline.ts. Only a
      transport failure counts; a real HTTP error isn't a connectivity signal. */
  onTransportResult?: (ok: boolean) => void;
}

// Fire-and-forget: arriving in the client app is what makes someone a
// patient, but establishing that must never block render or break the app
// if the call fails — the next mount retries.
//
// Gated on a verified session: AppShell can mount from a cache-hydrated
// (trust "cached") session, and firing this POST before the real /me lands
// would just be a mutation bound to fail (or double up once verified) — see
// the Phase 5 plan's "everything with a side effect waits for verified".
export function useEnsurePatientRelationship({
  onTransportResult,
}: UseEnsurePatientRelationshipOptions = {}): void {
  const api = useApi();
  const { trust } = useSession();

  useEffect(() => {
    if (trust !== "verified") return;
    api.patients
      .ensureRelationship()
      .then(() => onTransportResult?.(true))
      .catch((err) => {
        console.error("Failed to establish patient relationship", err);
        if (!(err instanceof ApiError)) onTransportResult?.(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, trust]);
}
